/**
 * The words for the eight states and the five severities.
 *
 * Exhaustive `Record`s, not lookups with a fallback. A ninth state added to the
 * server's enum is then a **compile error** in this file rather than a raw
 * `waiting_for_employee` rendered on a button an operator is being asked to press —
 * and `state-machine.test.ts`'s set comparison against the server's own table means
 * the compile error arrives in the same change that introduced the state.
 *
 * Separate from the components for one reason: three of them need the same map (the
 * list row, the transition control, the timeline), and a map copied three times is
 * two copies that drift towards whichever screen somebody edited last.
 */

import type { MessageKey } from '../i18n/messages';
import type { AlertSeverity } from '../api/types';
import type { Severity } from '../states/icons';
import type { IncidentState } from './state-machine';

export const STATE_LABEL: Readonly<Record<IncidentState, MessageKey>> = {
  new: 'incidents.state.new',
  acknowledged: 'incidents.state.acknowledged',
  investigating: 'incidents.state.investigating',
  waiting_for_employee: 'incidents.state.waitingForEmployee',
  resolved: 'incidents.state.resolved',
  false_positive: 'incidents.state.falsePositive',
  escalated: 'incidents.state.escalated',
  closed: 'incidents.state.closed',
};

/**
 * What pressing the control **does**, as a verb — not the name of the state.
 *
 * "Đã xử lý" is a label for a state and a bad label for a button, because a button
 * says what will happen. The two are kept apart deliberately: the state badge uses
 * `STATE_LABEL` and the transition control uses this, so the same word never has to
 * serve as both a description of where the incident is and an instruction about where
 * it is going.
 */
export const TRANSITION_LABEL: Readonly<Record<IncidentState, MessageKey>> = {
  new: 'incidents.move.new',
  acknowledged: 'incidents.move.acknowledged',
  investigating: 'incidents.move.investigating',
  waiting_for_employee: 'incidents.move.waitingForEmployee',
  resolved: 'incidents.move.resolved',
  false_positive: 'incidents.move.falsePositive',
  escalated: 'incidents.move.escalated',
  closed: 'incidents.move.closed',
};

export const SEVERITY_LABEL: Readonly<Record<AlertSeverity, MessageKey>> = {
  informational: 'severity.info',
  low: 'severity.low',
  medium: 'severity.medium',
  high: 'severity.high',
  critical: 'severity.critical',
};

/**
 * The badge severity a **state** is drawn at.
 *
 * Not the incident's own severity — that is a separate badge beside it. This one says
 * how much attention the *position in the workflow* wants: an untouched `new` and an
 * `escalated` are loud, a live investigation is medium, a concluded incident is
 * quiet. Icon and text carry it, as everywhere; the colour is the third channel.
 */
export const STATE_BADGE: Readonly<Record<IncidentState, Severity>> = {
  new: 'high',
  acknowledged: 'medium',
  investigating: 'medium',
  waiting_for_employee: 'medium',
  resolved: 'low',
  false_positive: 'low',
  escalated: 'critical',
  closed: 'info',
};
